import { Minus, Plus, Trash2, ShoppingCart, PauseCircle, Percent, Tag } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { rp, num, parseNum } from "../../lib/format";

const CartItem = ({ item, promo, onQty, onRemove }) => (
  <div className="flex items-center gap-3 py-3 fade-up" data-testid={`cart-item-${item.product.id}`}>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold truncate">{item.product.name}</div>
      <div className="text-xs text-muted-foreground font-mono">{rp(item.product.price)}</div>
      {promo?.discount > 0 && <div className="text-[11px] text-success font-medium truncate" data-testid={`cart-promo-${item.product.id}`}>{promo.name} -{rp(promo.discount)}</div>}
    </div>
    <div className="flex items-center gap-1">
      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => onQty(item.qty - 1)} data-testid={`cart-dec-${item.product.id}`}><Minus className="h-3 w-3" /></Button>
      <Input className="h-7 w-11 text-center px-1 font-mono text-sm" inputMode="numeric" value={item.qty}
        onChange={(e) => onQty(parseNum(e.target.value) || 1)} data-testid={`cart-qty-${item.product.id}`} />
      <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => onQty(item.qty + 1)} data-testid={`cart-inc-${item.product.id}`}><Plus className="h-3 w-3" /></Button>
    </div>
    <div className="w-24 text-right font-mono text-sm font-bold">{rp(item.product.price * item.qty - (promo?.discount || 0))}</div>
    <button onClick={onRemove} className="text-muted-foreground hover:text-destructive transition-colors" data-testid={`cart-remove-${item.product.id}`}><Trash2 className="h-4 w-4" /></button>
  </div>
);

export const CartPanel = ({ cart, promoMap = {}, setQty, remove, clear, discount, setDiscount, discType, setDiscType, totals, taxPercent, onPay, onHold, heldCount, onShowHeld }) => (
  <div className="flex flex-col h-full" data-testid="cart-panel">
    <div className="flex items-center justify-between pb-3 border-b">
      <div className="flex items-center gap-2">
        <ShoppingCart className="h-5 w-5 text-primary" />
        <h2 className="font-display font-bold">Keranjang</h2>
        <span className="text-xs rounded-full bg-primary/15 text-primary px-2 py-0.5 font-mono" data-testid="cart-count">{cart.reduce((a, b) => a + b.qty, 0)}</span>
      </div>
      <div className="flex gap-1">
        <Button size="sm" variant="ghost" onClick={onShowHeld} data-testid="show-held-btn">Ditahan ({heldCount})</Button>
        <Button size="sm" variant="ghost" className="text-destructive" onClick={clear} disabled={!cart.length} data-testid="clear-cart-btn">Kosongkan</Button>
      </div>
    </div>
    <div className="flex-1 overflow-y-auto thin-scroll divide-y min-h-[120px]">
      {cart.length === 0 ? (
        <div className="h-full grid place-items-center text-center text-muted-foreground py-10" data-testid="cart-empty">
          <div><ShoppingCart className="h-10 w-10 mx-auto opacity-30 mb-2" /><p className="text-sm">Scan barcode atau pilih produk</p></div>
        </div>
      ) : (
        cart.map((it) => <CartItem key={it.product.id} item={it} promo={promoMap[it.product.id]} onQty={(q) => setQty(it.product.id, q)} onRemove={() => remove(it.product.id)} />)
      )}
    </div>
    <div className="border-t pt-3 space-y-2">
      <div className="flex items-center gap-2">
        <Tag className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground flex-1">Diskon</span>
        <div className="flex rounded-md border overflow-hidden">
          <button className={`px-2 text-xs h-8 ${discType === "rp" ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setDiscType("rp")} data-testid="disc-type-rp">Rp</button>
          <button className={`px-2 h-8 ${discType === "pct" ? "bg-primary text-primary-foreground" : ""}`} onClick={() => setDiscType("pct")} data-testid="disc-type-pct"><Percent className="h-3 w-3" /></button>
        </div>
        <Input className="h-8 w-28 text-right font-mono" inputMode="numeric" value={discount ? num(discount) : ""} placeholder="0"
          onChange={(e) => setDiscount(parseNum(e.target.value))} data-testid="cart-discount-input" />
      </div>
      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{rp(totals.subtotal)}</span></div>
      {totals.discount > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Diskon</span><span className="font-mono text-destructive">-{rp(totals.discount)}</span></div>}
      {taxPercent > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Pajak ({taxPercent}%)</span><span className="font-mono">{rp(totals.tax)}</span></div>}
      <div className="flex justify-between items-end pt-1">
        <span className="font-semibold">Total</span>
        <span className="font-mono font-bold text-2xl text-primary" data-testid="cart-total">{rp(totals.total)}</span>
      </div>
      <div className="grid grid-cols-[auto_1fr] gap-2 pt-1">
        <Button variant="outline" className="h-12" onClick={onHold} disabled={!cart.length} data-testid="hold-cart-btn"><PauseCircle className="h-4 w-4 mr-1" />Tahan</Button>
        <Button className="h-12 text-base font-semibold" onClick={onPay} disabled={!cart.length} data-testid="checkout-button">Bayar (F9)</Button>
      </div>
    </div>
  </div>
);
